<template>
  <v-card
    class="integration-create-card"
    :class="{ 'disabled-card': disabled }"
    outlined
    hover
    @click="handleClick"
  >
    <div class="create-card-content">
      <!-- Icon -->
      <div class="create-icon-container">
        <v-icon
          :color="disabled ? 'grey' : 'primary'"
          size="48"
        >
          {{ disabled ? 'mdi-lock' : 'mdi-plus-circle' }}
        </v-icon>
      </div>      <!-- Content -->
      <div class="create-content">
        <!-- License Usage (when showUsage is true) -->
        <div v-if="showUsage" class="usage-info">
          <span class="usage-text">
            {{ currentUsage }} of {{ maxIntegrations }} integrations used
          </span>
          <v-progress-linear
            :value="usagePercentage"
            :color="usageColor"
            height="3"
            rounded
            class="usage-progress mt-1 mb-3"
          />
        </div>

        <h3 class="create-title">
          {{ disabled ? 'License Limit Reached' : 'Create New System' }}
        </h3>
        <p class="create-subtitle">
          {{ disabled ? 'Upgrade your license to add more integrations' : 'Connect a new integration to sync data automatically' }}
        </p>
      </div>

      <!-- Action Button -->
      <div class="create-action">
        <v-btn
          :disabled="disabled"
          :color="disabled ? 'grey' : 'primary'"
          :outlined="disabled"
          @click.stop="handleButtonClick"
        >
          {{ disabled ? 'Upgrade License' : 'Get Started' }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'

/**
 * Props for IntegrationCreateCard component
 */
export interface IntegrationCreateCardProps {
  /** Whether the create action is disabled (license limit reached) */
  disabled: boolean
  /** Current number of integrations used */
  currentUsage: number
  /** Maximum number of integrations allowed */
  maxIntegrations: number
  /** Whether to show the usage information */
  showUsage: boolean
}

/**
 * Events emitted by IntegrationCreateCard component
 */
export interface IntegrationCreateCardEvents {
  /** Emitted when user clicks to create a new integration */
  'create-integration': () => void
  /** Emitted when user clicks to upgrade license */
  'upgrade-license': () => void
}

export default {
  name: 'IntegrationCreateCard',

  props: {
    /** Whether the create action is disabled (license limit reached) */
    disabled: {
      type: Boolean,
      default: false
    },

    /** Current number of integrations used */
    currentUsage: {
      type: Number,
      default: 0
    },

    /** Maximum number of integrations allowed */
    maxIntegrations: {
      type: Number,
      default: 10
    },

    /** Whether to show the usage information */
    showUsage: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * Calculate usage percentage for progress bar
     */
    usagePercentage(): number {
      if (this.maxIntegrations === 0) return 0
      return (this.currentUsage / this.maxIntegrations) * 100
    },

    /**
     * Get color for usage progress bar based on usage level
     */
    usageColor(): string {
      const percentage = this.usagePercentage
      if (percentage >= 90) return 'error'
      if (percentage >= 75) return 'warning'
      return 'primary'
    }
  },

  methods: {
    /**
     * Handle card click
     */
    handleClick() {
      if (!this.disabled) {
        this.$emit('create-integration')
      }
    },

    /**
     * Handle button click
     */
    handleButtonClick() {
      if (this.disabled) {
        this.$emit('upgrade-license')
      } else {
        this.$emit('create-integration')
      }
    }
  }
}
</script>

<style scoped>
.integration-create-card {
  min-height: 120px;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 2px dashed var(--global-border-color, #e0e0e0);
  background-color: var(--global-muted, #fafafa);
}

.integration-create-card:hover:not(.disabled-card) {
  border-color: var(--global-primary, #1976d2);
  background-color: var(--global-card-background, #f5f9ff);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.disabled-card {
  cursor: not-allowed;
  opacity: 0.6;
}

.disabled-card:hover {
  transform: none;
  box-shadow: none;
}

.usage-info .usage-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--global-foreground, #424242);
}

.usage-progress {
  width: 100%;
  max-width: 200px;
}

.create-card-content {
  display: flex;
  align-items: center;
  padding: 24px;
  gap: 20px;
  height: 100%;
}

.create-icon-container {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--global-muted, rgba(25, 118, 210, 0.1));
}

.disabled-card .create-icon-container {
  background-color: var(--global-muted, rgba(158, 158, 158, 0.1));
}

.create-content {
  flex: 1;
  min-width: 0;
}

.create-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--global-foreground, #212121);
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.disabled-card .create-title {
  color: var(--global-muted-foreground, #757575);
}

.create-subtitle {
  font-size: 0.875rem;
  color: var(--global-muted-foreground, #616161);
  margin: 0;
  line-height: 1.4;
}

.disabled-card .create-subtitle {
  color: var(--global-muted-foreground, #9e9e9e);
}

.create-action {
  flex-shrink: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .create-card-content {
    flex-direction: column;
    text-align: center;
    padding: 20px 16px;
    gap: 16px;
  }

  .create-icon-container {
    width: 60px;
    height: 60px;
  }

  .create-title {
    font-size: 1rem;
  }

  .create-subtitle {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .create-card-content {
    padding: 16px 12px;
  }

  .create-icon-container {
    width: 50px;
    height: 50px;
  }

  .create-icon-container .v-icon {
    font-size: 32px !important;
  }
}
</style>

<!-- Unscoped styles for dark mode support -->
<style lang="scss">
/* Dark mode overrides for IntegrationCreateCard */
.dark,
.theme--dark {
  .integration-create-card.v-card {
    background-color: var(--global-card-background, #1c1c1e) !important;
    border-color: var(--global-border-color, #2e2e32) !important;
  }

  .integration-create-card:hover:not(.disabled-card) {
    background-color: rgba(255, 255, 255, 0.04) !important;
    border-color: var(--global-primary, #ed303e) !important;
  }

  .integration-create-card .create-icon-container {
    background-color: rgba(255, 255, 255, 0.08) !important;
  }
}
</style>
